import { Card } from "@volca/ui/components/ui/card";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";

interface LegendEntry {
  label: string;
  color: string;
}

interface DashboardChartCardProps {
  title: string;
  total?: number;
  caption?: string;
  legend?: LegendEntry[];
  isLoading?: boolean;
  errorMessage?: string;
  children: React.ReactNode;
}

/** A chart with its headline number: title and legend, total, then the plot. */
export const DashboardChartCard = ({
  title,
  total,
  caption,
  legend,
  isLoading,
  errorMessage,
  children,
}: DashboardChartCardProps) => (
  <Card className="min-w-0 flex-1 gap-0 p-4">
    <Stack className="w-full" align="start" gap={1}>
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap={2}
        className="w-full flex-wrap"
      >
        <Text variant="title">{title}</Text>
        {/* A plain key, not chips: it doesn't filter, and it stays within the
            title's line so both chart cards' plots start level. */}
        {legend && (
          <Stack align="center" direction="row" gap={3} className="flex-wrap">
            {legend.map((entry) => (
              <Stack key={entry.label} direction="row" align="center" gap={1}>
                <span
                  aria-hidden="true"
                  className="size-2 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <Text variant="caption" tone="muted">
                  {entry.label}
                </Text>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
      {isLoading ? (
        <Skeleton className="h-10 w-20" />
      ) : (
        !errorMessage && (
          <Stack direction="row" align="baseline" gap={2}>
            <Text variant="stat" tone="brand">
              {total}
            </Text>
            {caption && (
              <Text variant="caption" tone="muted">
                {caption}
              </Text>
            )}
          </Stack>
        )
      )}
    </Stack>
    <div className="pt-2">
      {isLoading && <Skeleton className="h-52" />}
      {!isLoading && errorMessage && (
        <Text tone="destructive" className="py-16 text-center">
          {errorMessage}
        </Text>
      )}
      {!isLoading && !errorMessage && children}
    </div>
  </Card>
);
